import {
  Grid,
  IconButton,
  SxProps,
  Table,
  TableContainer,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import { useAppSelector } from "../../../../../app/hooks";
import { ReactComponent as EditIcon } from "../../../../../common/icons/editDark.svg";
import { Nullable } from "../../../../../common/types/nullable";
import { customerOrgNamesResult } from "../../../../customer/customerAPI";
import {
  AccumDateData,
  AccumDateRow,
  AccumulationCalculation,
  AccumulationsForEmploymentResponse,
} from "../../../../employment/employmentApi";
import { selectAssignments } from "../../AssignmentAdditionalInfo/AssignmentsSlice";
import { getHeaderColumns } from "../AccumulationsModal";
import {
  getAssignmentName,
  isAccumulationCalculationMultiple,
  isAccumulationCalculationSingle,
} from "../helpers";
import { StyledTableBody, StyledTableCell } from "../styles";
import { SelectedAccumulation } from "../types";
import AccumulationsTableAdditionalTableCells from "./AccumulationsTableAdditionalTableCells";
import AccumulationsTableGroupMathTable from "./AccumulationsTableGroupMathTable";
import AccumulationsTableHeaderTableCells from "./AccumulationsTableHeaderTableCells";
import AccumulationsTableMultiCalculationTable from "./AccumulationsTableMultiCalculationTable";
import AccumulationsTableSingleCalculationTable from "./AccumulationsTableSingleCalculationTable";

type Props = {
  tableData: AccumulationsForEmploymentResponse;
  customerOrgNames: customerOrgNamesResult[];
  openConfimationDialog: (deltaId: string) => void;
  tableProps?: { sx: SxProps };
  selectedAccumulation: Nullable<SelectedAccumulation>;
  handleSelectedAccumulationRowChange: (row: AccumDateData) => void;
  onEdit: () => void;
  canEdit: boolean;
};

const AccumulationsTable = ({
  tableData,
  customerOrgNames,
  openConfimationDialog,
  tableProps,
  selectedAccumulation,
  handleSelectedAccumulationRowChange,
  onEdit,
  canEdit,
}: Props) => {
  const headerColumns = getHeaderColumns(tableData);
  const dataIncludesDelta = tableData?.data.some((row) =>
    row.rows.some((item) => item.deltaId),
  );
  const dataIncludesAssignmentId = tableData?.data.some((row) =>
    row.rows.some((item) => item.assignmentId),
  );

  const assignments = useAppSelector(selectAssignments);
  const { workTitle: workTitleTranslations } = useAppSelector(
    (state) => state.localized,
  );

  // stripes for delta rows
  const getRowBackgroundColor = (deltaId: string | undefined, index: number) =>
    deltaId ? (index % 2 === 0 ? "#eeebf5" : "#e3ddee") : "inherit";

  const renderTableHead = () => (
    <AccumulationsTableHeaderTableCells
      headerColumns={headerColumns}
      dataIncludesDelta={dataIncludesDelta}
      selectedAccumulation={selectedAccumulation}
    />
  );

  const renderJsonValue = (
    value: AccumulationCalculation,
    rowData?: AccumDateRow,
  ) => {
    // Special handling for GROUP_MATH - render combined table for lomakeskiansiolaskenta only
    // Skip rendering for "viikkokeskityömäärälaskenta" as it's combined into lomakeskiansiolaskenta display
    if (selectedAccumulation?.calculationType === "groupMath" && rowData) {
      const keyName = [...headerColumns.keys, ...headerColumns.stateNames].find(
        (key) => rowData[key as keyof AccumDateRow] === value,
      );

      // Find which state corresponds to ASSIGNMENT scope for assignment name display
      const stateNames = Array.isArray(selectedAccumulation.stateNames)
        ? selectedAccumulation.stateNames
        : [selectedAccumulation.stateNames];
      const scopes = Array.isArray(selectedAccumulation.scope)
        ? selectedAccumulation.scope
        : [selectedAccumulation.scope];

      const assignmentStateIndex = scopes.findIndex((scope) =>
        scope.includes("ASSIGNMENT"),
      );
      const assignmentStateName =
        assignmentStateIndex !== -1 ? stateNames[assignmentStateIndex] : null;

      if (keyName === assignmentStateName) {
        // Get assignment name for display
        const assignmentName = rowData?.assignmentId
          ? getAssignmentName(
              workTitleTranslations,
              assignments.find((a) => a.assignmentId === rowData.assignmentId),
            )
          : undefined;

        return (
          <AccumulationsTableGroupMathTable
            rowData={rowData}
            assignmentName={assignmentName || ""}
            tableData={tableData}
          />
        );
      } else if (
        keyName &&
        stateNames.includes(keyName) &&
        keyName !== assignmentStateName
      ) {
        // Hide other GROUP_MATH states as they're combined into the assignment state display
        return <StyledTableCell>-</StyledTableCell>;
      }
    }

    if (isAccumulationCalculationMultiple(value)) {
      const { ongoingAverages } = value;
      if (ongoingAverages) {
        const rows = Object.values(ongoingAverages);

        return <AccumulationsTableMultiCalculationTable rows={rows} />;
      }
    } else if (isAccumulationCalculationSingle(value)) {
      return <AccumulationsTableSingleCalculationTable value={value} />;
    }
  };

  const renderStringValue = (key: string, value?: string | number) => {
    switch (key) {
      case "assignmentId": {
        const selectedAssignment = assignments.find(
          (assignment) => assignment.assignmentId === value,
        );

        return (
          <StyledTableCell key={`TableCell_${key}_${value}`} align="center">
            {getAssignmentName(workTitleTranslations, selectedAssignment) ||
              value}
          </StyledTableCell>
        );
      }
      case "custOrgId":
        // replace custOrgId with custOrgName and remain object/rendering order
        // fallback to custOrgId if name is somehow not found
        const custOrgName: string | undefined = customerOrgNames.find(
          (custOrg) => custOrg.id === value,
        )?.name;

        return (
          <StyledTableCell key={`TableCell_${key}_${value}`} align="left">
            {custOrgName || value}
          </StyledTableCell>
        );
      case "custOrgName":
        return null;
      case "year":
      case "month":
      case "time":
      case "days":
        return (
          <StyledTableCell key={`TableCell_${key}_${value}`} align="center">
            {value}
          </StyledTableCell>
        );
      default:
        return (
          <StyledTableCell key={`TableCell_${key}_${value}`} align="right">
            {value}
          </StyledTableCell>
        );
    }
  };

  const renderTableBody = () => {
    const renderDateRow = (date: Nullable<string>, row: AccumDateData) => {
      // allow only one delta per date
      const rowIncludesDelta = row.rows.some((item) => item.deltaId);

      return (
        <TableRow>
          <StyledTableCell width="10%">
            {date && format(new Date(date), "dd.MM.yyyy")}
          </StyledTableCell>
          {/* table cell for edit icon */}
          <StyledTableCell align="right" />
          {dataIncludesAssignmentId && <StyledTableCell align="right" />}
          <StyledTableCell align="right">
            {selectedAccumulation?.isInternal && canEdit && (
              <IconButton
                disabled={rowIncludesDelta}
                sx={{ padding: "6px" }}
                onClick={() => {
                  handleSelectedAccumulationRowChange(row);
                  onEdit();
                }}
              >
                <EditIcon />
              </IconButton>
            )}
          </StyledTableCell>
        </TableRow>
      );
    };

    const renderValueRows = (rows: AccumDateRow[]) => {
      return rows.map((rowData: AccumDateRow, index: number) => {
        const backgroundColor = getRowBackgroundColor(rowData.deltaId, index);
        const tableRowSx = { "& > td": { backgroundColor } };
        return (
          <TableRow key={`TableRow_${index}`} sx={tableRowSx}>
            {/* empty cell for modification date column */}
            <StyledTableCell />
            {[...headerColumns.keys, ...headerColumns.stateNames].map((key) => {
              const keyOfAccumDateRow = key as keyof AccumDateRow;
              const cellValue = rowData[keyOfAccumDateRow];

              if (
                typeof cellValue === "string" ||
                typeof cellValue === "number" ||
                cellValue === undefined
              ) {
                return renderStringValue(key, cellValue);
              } else {
                return renderJsonValue(cellValue, rowData);
              }
            })}
            <AccumulationsTableAdditionalTableCells
              rowData={rowData}
              openConfimationDialog={openConfimationDialog}
              dataIncludesDelta={dataIncludesDelta}
              selectedAccumulation={selectedAccumulation}
              canEdit={canEdit}
            />
          </TableRow>
        );
      });
    };

    return tableData?.data.map((row, rowIndex) => (
      <StyledTableBody key={`TableBody_${row.date}_${rowIndex}`}>
        {renderDateRow(row.date, row)}
        {renderValueRows(row.rows)}
      </StyledTableBody>
    ));
  };

  return (
    <Grid item xs={12} sx={{ marginTop: "1rem" }}>
      <TableContainer {...tableProps}>
        <Table>
          {renderTableHead()}
          {renderTableBody()}
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default AccumulationsTable;
